import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { IconArrowRight } from '@xing-com/icons';

import { savedCardMap } from './card-map';
import { useSearchAndShuffleContext } from './context';
import {
  Container,
  Item,
  ListContainer,
  List,
  ItemContent,
  EmptyState,
} from './saved-cards.styled';
import { useTotalDecks } from './use-resize';

const addCardId = debounce(
  (cardId: string, renderedCards: React.MutableRefObject<string[]>) => {
    renderedCards.current.push(cardId);
  },
  1000
);

const checkIfNewCard = (
  cardId: string,
  renderedCards: React.MutableRefObject<string[]>
): boolean => {
  if (
    renderedCards.current.length > 0 &&
    !renderedCards.current.includes(cardId)
  ) {
    addCardId(cardId, renderedCards);
    return true;
  }
  return false;
};

export const SavedCards: React.FC = () => {
  const renderedCards = useRef<string[]>([]);
  const { savedCards, config, deletingSavedCards } =
    useSearchAndShuffleContext();
  const totalDecks = useTotalDecks();
  const { titleKey, url } = config.savedCards;

  useEffect(() => {
    if (savedCards.length > 0 && renderedCards.current.length === 0) {
      renderedCards.current = savedCards.map((card) => card.data.id);
    }
  }, [savedCards]);

  const displayingCards = savedCards.filter(
    (card) => deletingSavedCards.includes(card.data.id) === false
  );

  return (
    <Container $totalDecks={totalDecks} $hide={displayingCards.length === 0}>
      <TextButton size="large" icon={IconArrowRight} iconAfter href={url}>
        <FormattedMessage id={titleKey} defaultMessage={titleKey} />
      </TextButton>
      <ListContainer>
        <List>
          {savedCards.map((card, index) => {
            const isNew = checkIfNewCard(card.data.id, renderedCards);
            const CardComponent = savedCardMap[card?.type]
              ? savedCardMap[card.type]
              : () => null;

            return (
              <Item
                key={card.data.id || index}
                $isNew={isNew}
                $isDeleting={deletingSavedCards.includes(card.data.id)}
              >
                <ItemContent>
                  <CardComponent card={card} />
                </ItemContent>
              </Item>
            );
          })}
          {Array.from({ length: totalDecks - displayingCards.length }).map(
            (_, idx) => (
              <EmptyState key={`empty-${idx}`} />
            )
          )}
        </List>
      </ListContainer>
    </Container>
  );
};
